import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, OnChanges, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';

interface TrainingPlanStatusSlice {
  label: string;
  value: number;
}

@Component({
  selector: 'app-training-planchart',
  imports: [
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    CommonModule,
    FormsModule
  ],
  templateUrl: './training-planchart.component.html',
  styleUrl: './training-planchart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingPlanchartComponent implements OnChanges, AfterViewInit {
  @Input() statusData: any[] | null = null;

  private isBrowser: boolean;
  private viewInitialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.isBrowser) {
      void this.loadChart(this.getChartSeries(this.statusData));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['statusData'] && this.isBrowser && this.viewInitialized) {
      void this.loadChart(this.getChartSeries(this.statusData));
    }
  }

  async loadChart(chartData: TrainingPlanStatusSlice[]): Promise<void> {
    try {
      const ApexCharts = (await import('apexcharts')).default;
      const isDefaultChart = this.isDefaultSeries(chartData);
      const visibleChartData = isDefaultChart ? chartData : chartData.filter((item) => item.value > 0);
      const finalChartData = visibleChartData.length ? visibleChartData : this.getDefaultSeries();

      const options = {
        series: finalChartData.map((item) => item.value),
        chart: { height: 360, type: 'pie' },
        labels: finalChartData.map((item) => item.label),
        colors: ['#07146f', '#f1bd00', '#157e00', '#a90000', '#a92cd0', '#0f766e', '#ea580c', '#6b7280'],
        legend: {
          show: true,
          fontSize: '12px',
          position: 'bottom',
          horizontalAlign: 'left',
          itemMargin: { horizontal: 12, vertical: 6 },
          labels: { colors: '#64748B' },
          markers: { size: 7, offsetX: -2, offsetY: -0.5, shape: 'diamond' },
          formatter: (seriesName: string, opts: any) => `${seriesName}: ${opts.w.globals.series[opts.seriesIndex]}`
        },
        dataLabels: {
          enabled: true,
          formatter: (_val: number, opts: any) => `${opts.w.globals.series[opts.seriesIndex]}`,
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        }
      };

      const chartEl = document.querySelector('#basic_pie_chart');

      if (chartEl && (chartEl as any).__apexChart) {
        (chartEl as any).__apexChart.destroy();
      }

      const chart = new ApexCharts(chartEl, options);
      (chartEl as any).__apexChart = chart;
      chart.render();
    } catch (error) {
      console.error('Error loading ApexCharts:', error);
    }
  }

  private getChartSeries(data: any[] | null): TrainingPlanStatusSlice[] {
    const counts = new Map<string, number>(
      this.getStatusBuckets().map((item) => [item.label, 0])
    );

    if (!Array.isArray(data) || data.length === 0) {
      return this.getDefaultSeries();
    }

    const uniqueItems = new Map<string, any>();

    for (const item of data) {
      const uniqueKey = String(item?.id ?? `${item?.trainingPlanCode ?? ''}-${item?.laststatus ?? item?.paymentDisbursementLaststatus ?? item?.lastStatus ?? ''}`);
      if (!uniqueItems.has(uniqueKey)) {
        uniqueItems.set(uniqueKey, item);
      }
    }

    for (const item of uniqueItems.values()) {
      const label = this.getStatusLabel(item);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }

    return Array.from(counts.entries()).map(([label, value]) => ({ label, value }));
  }

  private getStatusLabel(item: any): string {
    return this.getStatusFromId(item?.laststatus ?? item?.paymentDisbursementLaststatus ?? item?.lastStatus)
      || 'Unknown';
  }

  private getStatusFromId(statusId: unknown): string | null {
    switch (Number(statusId)) {
      case 30:
        return 'Training Approved';
      case 70:
        return 'Training In-Progress';
      case 80:
        return 'Training Completed';
      case 90:
        return 'Payment Request';
      case 100:
        return 'Payment Approved';
      case 110:
        return 'Payment Reversed Back';
      case 120:
        return 'Payment Disbursed';
      case 50:
        return 'Canceled';
      default:
        return null;
    }
  }

  private getDefaultSeries(): TrainingPlanStatusSlice[] {
    return [
      { label: 'Planned', value: 55.4 },
      { label: 'In Process', value: 14.9 },
      { label: 'Reversed', value: 14.9 },
      { label: 'Rejected', value: 14.9 },
      { label: 'Approved', value: 25 }
    ];
  }

  private getStatusBuckets(): TrainingPlanStatusSlice[] {
    return [
      { label: 'Training Approved', value: 0 },
      { label: 'Training In-Progress', value: 0 },
      { label: 'Training Completed', value: 0 },
      { label: 'Payment Request', value: 0 },
      { label: 'Payment Approved', value: 0 },
      { label: 'Payment Reversed Back', value: 0 },
      { label: 'Payment Disbursed', value: 0 },
      { label: 'Canceled', value: 0 }
    ];
  }

  private isDefaultSeries(data: TrainingPlanStatusSlice[]): boolean {
    const defaultSeries = this.getDefaultSeries();
    if (data.length !== defaultSeries.length) {
      return false;
    }

    return data.every((item, index) =>
      item.label === defaultSeries[index].label && item.value === defaultSeries[index].value
    );
  }
}
